import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Download,
  RefreshCw,
  Package,
  Users,
  CheckCircle,
  DollarSign,
  Calendar,
  AlertCircle,
  Printer,
  Loader2,
  ListChecks,
} from 'lucide-react';
import { Button, Input, SearchSelect, StatsGrid } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import {
  exportAdminServicesReportCsv,
  useAdminServicesReport,
} from '../../services/adminServicesReportService';
import { useAdminVendors } from '../../hooks/useAdminVendors';
import { useServiceCategories } from '../../services/serviceCategoryService';
import { useToastContext } from '../../contexts/ToastContext';
import PrivateBookingBadge from '../../components/shared/PrivateBookingBadge';
import { isPrivateBooking, PRIVATE_PAYOUT_EXCLUDED_NOTE } from '../../utils/bookingSource';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';

const PERIOD_OPTIONS = [
  { value: 'week', label: 'Last 7 days' },
  { value: 'month', label: 'Last 30 days' },
  { value: 'quarter', label: 'Last quarter' },
  { value: 'year', label: 'Last year' },
  { value: 'all', label: 'All time' },
  { value: 'custom', label: 'Custom range' },
];

const DATE_FIELD_OPTIONS = [
  { value: 'event', label: 'Event date' },
  { value: 'created', label: 'Booking created date' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'completed', label: 'Completed only' },
  { value: 'confirmed,in_progress', label: 'Confirmed & in progress' },
  { value: 'pending', label: 'Pending only' },
  { value: 'cancelled', label: 'Cancelled only' },
];

const VIEW_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'vendors', label: 'By vendor' },
  { id: 'services', label: 'By service' },
  { id: 'bookings', label: 'All bookings' },
];

const formatMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatStatus = (s) =>
  s ? s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '—';

const PrintDataTable = ({ title, headers, rows, alignRight = [], className = '' }) => (
  <section className={`srp-section ${className}`}>
    <h2 className="srp-section-title">{title}</h2>
    {rows.length === 0 ? (
      <p className="srp-empty">No records for the selected criteria.</p>
    ) : (
      <table className="srp-data-table">
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={h} className={alignRight.includes(i) ? 'srp-num' : ''}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIdx) => (
            <tr key={rowIdx}>
              {row.map((cell, cellIdx) => (
                <td key={cellIdx} className={alignRight.includes(cellIdx) ? 'srp-num' : ''}>
                  {cell ?? '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </section>
);

const ServicesReportPrintDocument = ({ report, summary, filterSummaryText, filters }) => {
  const generatedAt = report?.metadata?.generated_at
    ? new Date(report.metadata.generated_at).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : new Date().toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

  const criteriaRows = [
    ['Time period', filterSummaryText.periodLabel],
    ['Date range', filterSummaryText.range || '—'],
    ['Date field', filterSummaryText.dateFieldLabel],
    ['Vendor', filterSummaryText.vendorLabel],
    ['Category', filterSummaryText.categoryLabel],
    ['Booking status', filterSummaryText.statusLabel],
  ];
  if (filters.search?.trim()) {
    criteriaRows.push(['Search', filters.search.trim()]);
  }

  const kpis = [
    { label: 'Total bookings', value: summary.total_bookings ?? 0 },
    { label: 'Completed', value: summary.completed_bookings ?? 0 },
    { label: 'Completion rate', value: `${summary.completion_rate ?? 0}%` },
    { label: 'Unique vendors', value: summary.unique_vendors ?? 0 },
    { label: 'Services booked', value: summary.unique_services ?? 0 },
    { label: 'Amount collected', value: formatMoney(summary.total_collected) },
    { label: 'Vendor payouts', value: formatMoney(summary.total_vendor_payout) },
    { label: 'Platform fees', value: formatMoney(summary.total_platform_fees) },
    ...(summary.private_bookings_count > 0
      ? [
          { label: 'Private bookings', value: summary.private_bookings_count },
          { label: 'Private offline collected', value: formatMoney(summary.private_offline_collected) },
        ]
      : []),
  ];

  return (
    <div id="services-report-print-area" className="services-report-print-document">
      <header className="srp-header">
        <div>
          <span className="srp-brand-name">PhotoBooth</span>
          <span className="srp-brand-tag">Services Report</span>
        </div>
        <div className="srp-meta">
          <p>
            <strong>Generated:</strong> {generatedAt}
          </p>
          <p>
            <strong>Records:</strong> {report?.metadata?.row_count ?? 0} booking(s)
          </p>
        </div>
      </header>

      <section className="srp-section">
        <h2 className="srp-section-title">Report criteria</h2>
        <table className="srp-criteria-table">
          <tbody>
            {criteriaRows.map(([label, value]) => (
              <tr key={label}>
                <th>{label}</th>
                <td>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="srp-section">
        <h2 className="srp-section-title">Executive summary</h2>
        <div className="srp-kpi-grid">
          {kpis.map((kpi) => (
            <div key={kpi.label} className="srp-kpi">
              <p className="srp-kpi-label">{kpi.label}</p>
              <p className="srp-kpi-value">{kpi.value}</p>
            </div>
          ))}
        </div>
      </section>

      <PrintDataTable
        title="Bookings by status"
        headers={['Status', 'Count']}
        alignRight={[1]}
        rows={Object.entries(report?.by_status || {}).map(([status, count]) => [
          formatStatus(status),
          count,
        ])}
      />

      <PrintDataTable
        title="Performance by vendor"
        headers={['Vendor', 'Location', 'Bookings', 'Completed', 'Collected', 'Payout']}
        alignRight={[2, 3, 4, 5]}
        rows={(report?.vendor_breakdown || []).map((v) => [
          v.vendor_name,
          v.location || '—',
          v.total_bookings,
          v.completed_bookings,
          formatMoney(v.amount_collected),
          formatMoney(v.vendor_payout),
        ])}
      />

      <PrintDataTable
        title="Performance by service"
        headers={['Service', 'Category', 'Vendor', 'Bookings', 'Completed', 'Collected']}
        alignRight={[3, 4, 5]}
        rows={(report?.service_breakdown || []).map((s) => [
          s.service_name,
          s.category_name,
          s.vendor_name,
          s.total_bookings,
          s.completed_bookings,
          formatMoney(s.amount_collected),
        ])}
      />

      <PrintDataTable
        className="srp-section-bookings"
        title="Booking detail"
        headers={[
          'Reference',
          'Event date',
          'Status',
          'Type',
          'Vendor',
          'Service',
          'Client',
          'Paid',
          'Payout',
        ]}
        alignRight={[7, 8]}
        rows={(report?.booking_lines || []).map((b) => [
          b.booking_reference,
          b.event_date,
          formatStatus(b.status),
          b.is_private_booking ? 'Private' : 'Platform',
          b.vendor_name,
          b.service_name,
          b.client_name,
          formatMoney(b.amount_paid),
          b.is_private_booking ? '—' : formatMoney(b.vendor_payout_amount),
        ])}
      />

      <footer className="srp-footer">
        PhotoBooth Services Report · Confidential · {generatedAt}
      </footer>
    </div>
  );
};

const AdminServicesReport = () => {
  const { success, error: showError } = useToastContext();
  const [activeTab, setActiveTab] = useState('overview');
  const [exporting, setExporting] = useState(false);

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const monthAgo = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().slice(0, 10);
  }, []);

  const [filters, setFilters] = useState({
    period: 'month',
    date_from: monthAgo,
    date_to: today,
    date_field: 'event',
    vendor_id: 'all',
    category_id: 'all',
    status: 'all',
    search: '',
  });

  const { data: reportResponse, isLoading, error, refetch, isFetching } = useAdminServicesReport(filters);
  const report = reportResponse?.data;

  const { data: vendorsResponse } = useAdminVendors({ per_page: 500 });
  const { data: categoriesResponse } = useServiceCategories({});

  const vendorOptions = useMemo(() => {
    const vendors = vendorsResponse?.data?.data || vendorsResponse?.data || [];
    const opts = [{ value: 'all', label: 'All vendors' }];
    if (!Array.isArray(vendors)) return opts;
    vendors
      .filter((v) => v?.vendor_profile)
      .forEach((v) => {
        const vp = v.vendor_profile;
        opts.push({
          value: vp.id,
          label: vp.business_name || v.name || 'Vendor',
        });
      });
    return opts.sort((a, b) => (a.label > b.label ? 1 : -1));
  }, [vendorsResponse]);

  const categoryOptions = useMemo(() => {
    const cats = categoriesResponse?.data?.data || categoriesResponse?.data || [];
    const opts = [{ value: 'all', label: 'All categories' }];
    if (Array.isArray(cats)) {
      cats.forEach((c) => {
        if (c?.id) opts.push({ value: c.id, label: c.category_name || c.name });
      });
    }
    return opts;
  }, [categoriesResponse]);

  const filterSummaryText = useMemo(() => {
    const periodLabel = PERIOD_OPTIONS.find((p) => p.value === filters.period)?.label || filters.period;
    const dateFieldLabel =
      filters.date_field === 'event' ? 'Event date' : 'Booking created date';
    const vendorLabel =
      vendorOptions.find((v) => v.value === filters.vendor_id)?.label || 'All vendors';
    const categoryLabel =
      categoryOptions.find((c) => c.value === filters.category_id)?.label || 'All categories';
    const statusLabel = STATUS_OPTIONS.find((s) => s.value === filters.status)?.label || 'All statuses';
    const range =
      report?.filters?.date_from && report?.filters?.date_to
        ? `${report.filters.date_from} → ${report.filters.date_to}`
        : '';
    return { periodLabel, dateFieldLabel, vendorLabel, categoryLabel, statusLabel, range };
  }, [filters, vendorOptions, categoryOptions, report?.filters]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handlePrint = useCallback(() => {
    document.body.classList.add('services-report-printing');
    const cleanup = () => {
      document.body.classList.remove('services-report-printing');
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    window.print();
  }, []);

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await exportAdminServicesReportCsv(filters);
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `services-report-${filters.date_from}-to-${filters.date_to}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      success('Report exported successfully');
    } catch (err) {
      showError(err?.message || 'Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  const vendorColumns = [
    {
      key: 'vendor_name',
      label: 'Vendor',
      render: (row) => (
        <div>
          <p className="font-medium text-gray-900 dark:text-gray-100">{row.vendor_name}</p>
          {row.location && <p className="text-xs text-gray-500">{row.location}</p>}
        </div>
      ),
    },
    { key: 'total_bookings', label: 'Bookings', sortable: true },
    { key: 'completed_bookings', label: 'Completed', sortable: true },
    {
      key: 'services_count',
      label: 'Services used',
      sortable: true,
      render: (row) => row.services_count ?? row.services?.length ?? 0,
    },
    {
      key: 'amount_collected',
      label: 'Collected',
      sortable: true,
      render: (row) => formatMoney(row.amount_collected),
    },
    {
      key: 'vendor_payout',
      label: 'Vendor payout',
      sortable: true,
      render: (row) => formatMoney(row.vendor_payout),
    },
  ];

  const serviceColumns = [
    { key: 'service_name', label: 'Service', sortable: true, searchable: true },
    { key: 'category_name', label: 'Category', sortable: true },
    { key: 'vendor_name', label: 'Vendor', sortable: true },
    { key: 'total_bookings', label: 'Bookings', sortable: true },
    { key: 'completed_bookings', label: 'Completed', sortable: true },
    {
      key: 'amount_collected',
      label: 'Collected',
      render: (row) => formatMoney(row.amount_collected),
    },
  ];

  const bookingColumns = [
    {
      key: 'booking_reference',
      label: 'Reference',
      render: (row) => (
        <div className="flex flex-col gap-1">
          <Link to={`/admin/bookings/${row.id}`} className="text-primary-600 hover:underline font-medium">
            {row.booking_reference}
          </Link>
          {isPrivateBooking(row) && <PrivateBookingBadge />}
        </div>
      ),
    },
    { key: 'event_date', label: 'Event date', sortable: true },
    {
      key: 'status',
      label: 'Status',
      render: (row) => (
        <span className="text-xs font-medium capitalize">{formatStatus(row.status)}</span>
      ),
    },
    { key: 'vendor_name', label: 'Vendor', searchable: true },
    { key: 'service_name', label: 'Service', searchable: true },
    { key: 'category_name', label: 'Category' },
    { key: 'client_name', label: 'Client', searchable: true },
    {
      key: 'amount_paid',
      label: 'Paid',
      render: (row) => formatMoney(row.amount_paid),
    },
    {
      key: 'vendor_payout_amount',
      label: 'Payout',
      render: (row) =>
        isPrivateBooking(row) ? (
          <div>
            <span className="text-gray-400">$0.00</span>
            <p className="text-xs text-sky-600 dark:text-sky-400">{PRIVATE_PAYOUT_EXCLUDED_NOTE}</p>
          </div>
        ) : (
          formatMoney(row.vendor_payout_amount)
        ),
    },
  ];

  if (error) {
    return (
      <div className="space-y-5">
        <AdminPageHeader
          title="Services report"
          subtitle="See which vendors delivered which services, booking volumes, and revenue for any date range."
        />
        <div className="rounded-2xl border border-red-200 dark:border-red-800/50 bg-white dark:bg-gray-900 p-6 text-center shadow-sm">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <p className="text-gray-700 dark:text-gray-300">Failed to load services report.</p>
          <Button onClick={() => refetch()} className="mt-4 !rounded-xl">
            Try again
          </Button>
        </div>
      </div>
    );
  }

  const summary = report?.summary || {};

  return (
  <>
    <div className="services-report-screen space-y-5">
      <AdminPageHeader
        title="Services report"
        subtitle="See which vendors delivered which services, booking volumes, and revenue for any date range. Filter by vendor, category, or status, then print or export to CSV. Payout totals exclude private bookings."
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => refetch()}
              disabled={isFetching}
              className="inline-flex items-center gap-2 !rounded-xl"
            >
              <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="secondary"
              onClick={handlePrint}
              disabled={isLoading || !report}
              className="inline-flex items-center gap-2 !rounded-xl"
            >
              <Printer className="w-4 h-4" />
              Print
            </Button>
            <Button
              onClick={handleExport}
              disabled={exporting || isLoading || !report?.booking_lines?.length}
              className="inline-flex items-center gap-2 !rounded-xl"
            >
              <Download className="w-4 h-4" />
              {exporting ? 'Exporting…' : 'Export CSV'}
            </Button>
          </>
        }
      />

      {report?.metadata?.generated_at && (
        <p className="text-xs text-gray-500 -mt-2">
          Generated {new Date(report.metadata.generated_at).toLocaleString()} ·{' '}
          {report.metadata.row_count ?? 0} booking(s)
        </p>
      )}

      <AdminGuideCallout
        title={ADMIN_PAGE_GUIDES.servicesReport.title}
        steps={ADMIN_PAGE_GUIDES.servicesReport.steps}
        icon={ListChecks}
      />

      <div className="rounded-2xl border border-gray-200/80 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5 space-y-4">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Report filters</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <SearchSelect
            label="Time period"
            value={filters.period}
            onChange={(v) => updateFilter('period', v)}
            options={PERIOD_OPTIONS}
            searchable={false}
          />
          <SearchSelect
            label="Date based on"
            value={filters.date_field}
            onChange={(v) => updateFilter('date_field', v)}
            options={DATE_FIELD_OPTIONS}
            searchable={false}
          />
          <SearchSelect
            label="Vendor"
            value={filters.vendor_id}
            onChange={(v) => updateFilter('vendor_id', v)}
            options={vendorOptions}
            searchable
          />
          <SearchSelect
            label="Category"
            value={filters.category_id}
            onChange={(v) => updateFilter('category_id', v)}
            options={categoryOptions}
            searchable
          />
          <SearchSelect
            label="Booking status"
            value={filters.status}
            onChange={(v) => updateFilter('status', v)}
            options={STATUS_OPTIONS}
            searchable={false}
          />
          <div className="sm:col-span-2">
            <Input
              label="Search"
              value={filters.search}
              onChange={(e) => updateFilter('search', e.target.value)}
              placeholder="Reference, vendor, service, client…"
            />
          </div>
        </div>

        {filters.period === 'custom' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-gray-100 dark:border-gray-800">
            <Input
              label="From"
              type="date"
              value={filters.date_from}
              onChange={(e) => updateFilter('date_from', e.target.value)}
            />
            <Input
              label="To"
              type="date"
              value={filters.date_to}
              onChange={(e) => updateFilter('date_to', e.target.value)}
            />
          </div>
        )}

        {report?.filters && (
          <p className="text-xs text-gray-500">
            Showing bookings with{' '}
            {filters.date_field === 'event' ? 'event dates' : 'created dates'} from{' '}
            <strong>{report.filters.date_from}</strong> to <strong>{report.filters.date_to}</strong>
          </p>
        )}
      </div>

      {isLoading ? (
        <div className="rounded-2xl border border-gray-200/80 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm py-20 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Building report…</p>
        </div>
      ) : (
        <>
          <StatsGrid
            columns={4}
            stats={[
              {
                key: 'bookings',
                title: 'Total bookings',
                value: summary.total_bookings ?? 0,
                icon: Calendar,
              },
              {
                key: 'completed',
                title: 'Completed',
                value: summary.completed_bookings ?? 0,
                icon: CheckCircle,
                variant: 'success',
              },
              {
                key: 'vendors',
                title: 'Vendors',
                value: summary.unique_vendors ?? 0,
                icon: Users,
              },
              {
                key: 'collected',
                title: 'Collected',
                value: formatMoney(summary.total_collected),
                icon: DollarSign,
                variant: 'warning',
              },
            ]}
          />

          <StatsGrid
            columns={4}
            stats={[
              {
                key: 'services',
                title: 'Services booked',
                value: summary.unique_services ?? 0,
                icon: Package,
              },
              {
                key: 'rate',
                title: 'Completion rate',
                value: `${summary.completion_rate ?? 0}%`,
                icon: CheckCircle,
              },
              {
                key: 'payout',
                title: 'Vendor payouts (platform)',
                value: formatMoney(summary.total_vendor_payout),
                icon: DollarSign,
              },
              {
                key: 'fees',
                title: 'Platform fees',
                value: formatMoney(summary.total_platform_fees),
                icon: DollarSign,
              },
              ...(summary.private_bookings_count > 0
                ? [
                    {
                      key: 'private',
                      title: 'Private offline collected',
                      value: formatMoney(summary.private_offline_collected),
                      icon: DollarSign,
                      variant: 'info',
                    },
                  ]
                : []),
            ]}
          />

          {(summary.private_bookings_count ?? 0) > 0 && (
            <p className="text-xs text-sky-600 dark:text-sky-400 -mt-2">
              {summary.private_bookings_count} private booking(s) collected{' '}
              {formatMoney(summary.private_offline_collected)} offline — {PRIVATE_PAYOUT_EXCLUDED_NOTE.toLowerCase()}.
            </p>
          )}

          <div
            className="inline-flex flex-wrap rounded-full bg-gray-100 dark:bg-gray-800 p-1 border border-gray-200/80 dark:border-gray-700"
            role="tablist"
            aria-label="Report views"
          >
            {VIEW_TABS.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-3.5 py-2 rounded-full text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-50 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-gray-200/80 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  By status
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(report?.by_status || {}).map(([status, count]) => (
                    <div
                      key={status}
                      className="flex justify-between items-center p-2 rounded-xl bg-gray-50 dark:bg-gray-800/60"
                    >
                      <span className="text-sm capitalize text-gray-600 dark:text-gray-400">
                        {formatStatus(status)}
                      </span>
                      <span className="font-semibold text-gray-900 dark:text-gray-100 tabular-nums">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl border border-gray-200/80 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  Top vendors (by bookings)
                </h3>
                <ul className="space-y-2">
                  {(report?.top_vendors || []).length === 0 && (
                    <li className="text-sm text-gray-500">No data for this period.</li>
                  )}
                  {(report?.top_vendors || []).map((v, i) => (
                    <li
                      key={v.vendor_id || i}
                      className="flex justify-between text-sm py-1 border-b border-gray-100 dark:border-gray-800 last:border-0"
                    >
                      <span className="font-medium text-gray-900 dark:text-gray-100 truncate pr-2">
                        {v.vendor_name}
                      </span>
                      <span className="text-gray-600 dark:text-gray-400 shrink-0 tabular-nums">
                        {v.total_bookings} · {formatMoney(v.amount_collected)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {activeTab === 'vendors' && (
            <DataTable
              data={report?.vendor_breakdown || []}
              columns={vendorColumns}
              loading={isFetching}
              searchable={false}
              filterable={false}
              pageSize={15}
              emptyMessage="No vendor activity for the selected filters."
            />
          )}

          {activeTab === 'services' && (
            <DataTable
              data={report?.service_breakdown || []}
              columns={serviceColumns}
              loading={isFetching}
              pageSize={15}
              emptyMessage="No services found for the selected filters."
            />
          )}

          {activeTab === 'bookings' && (
            <DataTable
              data={report?.booking_lines || []}
              columns={bookingColumns}
              loading={isFetching}
              pageSize={20}
              searchPlaceholder="Filter table…"
              emptyMessage="No bookings match your filters."
            />
          )}
        </>
      )}
    </div>

    {!isLoading && report && (
      <ServicesReportPrintDocument
        report={report}
        summary={summary}
        filterSummaryText={filterSummaryText}
        filters={filters}
      />
    )}
  </>
  );
};

export default AdminServicesReport;
